import { View, StyleSheet } from "react-native";
import SectionHeader from "../../../SectionHeader/SectionHeader";
import ContentScrollBar from "../../../ScrollBar/ContentScrollBar";
import { ContentSection, ContentType } from "../cmsTypes";
import CmsHero from "./CmsHero";
import { MagazineComponent } from "./MagazineComponent";
import { useResponsive } from "../../../../hooks/useResponsive";
import Gap from "../../../Gap";
import { useRoute } from "@react-navigation/native";
import React from "react";
import { useAtom } from "jotai";
import { globalFullscreenAtom } from "../../../../atoms";

export const ContentSections = ({
  sections,
  only,
  except,
  loadingOverride,
}: {
  sections: ContentSection[];
  only?: ContentType;
  except?: ContentType[];
  loadingOverride?: boolean;
}) => {
  const { isMediumAndUp } = useResponsive();
  const route = useRoute();
  const isOnHomePage = route.name === "Home";

  const [globalFullscreen] = useAtom(globalFullscreenAtom);

  if (!sections?.length) return null;
  if (only) sections = sections.filter((s) => s.content_type == only);
  if (except)
    sections = sections.filter((s) => !except.includes(s.content_type));

  return (
    <React.Fragment>
      {sections
        .map((section) => {
          switch (section.content_type) {
            case "free_bets":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`free-bets-${section.title}`}
                >
                  <SectionHeader
                    heading={section.title}
                    colorTheme={isOnHomePage ? "tertiary" : "secondary"}
                    showMore={{ navigateTo: "free-bets", text: "View All" }}
                  />
                  <ContentScrollBar
                    type={"FreeBets"}
                    key={`free-bets-${section.title}-scroll-bar`}
                    contentLoadingOverride={loadingOverride}
                  />
                </View>
              );

            case "hero":
              return (
                <View key={`member-hero-${section.description}`}>
                  <CmsHero section={section} />
                </View>
              );
            case "club_days":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`club-days-${section.description}`}
                ></View>
              );
            case "members_benefits":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`member-benefit-${section.title}`}
                >
                  <SectionHeader
                    heading={section.title}
                    colorTheme={isOnHomePage ? "tertiary" : "secondary"}
                  />
                  <ContentScrollBar
                    type={"MembersBenefits"}
                    key={`member-benefit-${section.title}-scroll-bar`}
                    contentLoadingOverride={loadingOverride}
                  />
                </View>
              );
            case "members_magazine":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`magazine-view-${section.title}`}
                >
                  <MagazineComponent />
                </View>
              );
            case "racecards":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`racecards-${section.title}`}
                >
                  <SectionHeader
                    heading={section.title}
                    showMore={{ navigateTo: "Racecards", text: "View All" }}
                    size={isMediumAndUp ? "xl" : "lg"}
                    colorTheme={isOnHomePage ? "tertiary" : "secondary"}
                  />
                  <ContentScrollBar
                    type="UpcomingRaces"
                    key={`racecards-${section.title}-scroll-bar`}
                    contentLoadingOverride={loadingOverride}
                  />
                </View>
              );
            case "race_replays":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`race-replays-${section.title}`}
                >
                  <SectionHeader
                    heading={section.title}
                    showMore={{ navigateTo: "Replays", text: "View All" }}
                    size={isMediumAndUp ? "xl" : "lg"}
                    colorTheme={isOnHomePage ? "tertiary" : "secondary"}
                  />
                  <ContentScrollBar
                    type="Replays"
                    key={`race-replays-${section.title}-scroll-bar`}
                  />
                </View>
              );

            case "demand_videos":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`on-demand-videos-${section.title}`}
                >
                  <SectionHeader
                    heading={section.title}
                    showMore={{ navigateTo: "OnDemand", text: "View All" }}
                    size={isMediumAndUp ? "xl" : "lg"}
                    colorTheme={isOnHomePage ? "tertiary" : "secondary"}
                  />
                  <ContentScrollBar
                    type="OnDemand"
                    key={`on-demand-videos-${section.title}-scroll-bar`}
                    contentLoadingOverride={loadingOverride}
                  />
                </View>
              );
            case "news_articles":
              return (
                <View
                  style={{
                    ...(globalFullscreen && styles.fullScreen),
                  }}
                  key={`news_articles-${section.title}`}
                >
                  <SectionHeader
                    heading={section.title}
                    showMore={{ navigateTo: "Latest", text: "View All" }}
                    colorTheme={isOnHomePage ? "tertiary" : "secondary"}
                  />
                  <ContentScrollBar
                    type="NewsArticles"
                    contentLoadingOverride={loadingOverride}
                    key={`news-articles-${section.title}-scroll-bar`}
                  />
                </View>
              );
            default:
              return null;
          }
        })
        // add gaps between sections - its not possible with a join
        .reduce(
          (prev: JSX.Element, curr: JSX.Element, idx: number): any => [
            prev || [],
            curr,
            <Gap vertical size="small" key={`content-section-gap-${idx}`} />,
          ],
          []
        )}
    </React.Fragment>
  );
};

const styles = StyleSheet.create({
  fullScreen: {
    display: "none",
  },
});
